import apiClient from './meta-client';
import type {
    TemplateLibraryListResponse,
    TemplateLibraryFilters,
    TemplateFavoriteIdsResponse,
    TemplateFavoriteToggleResponse,
    TemplateFolderTree,
    CustomTemplate,
    CustomTemplateListResponse,
    CustomTemplateCreatePayload,
    CustomTemplateUpdatePayload,
    CustomTemplateClonePayload,
    CustomTemplateBulkCreatePayload,
    CustomTemplateBulkCreateResponse,
    CustomTemplateBatchMoveResponse,
} from '@/types/template-library';

export const listLibraryTemplates = async (
    filters: TemplateLibraryFilters = {},
): Promise<TemplateLibraryListResponse> => {
    const params: Record<string, any> = {};
    if (filters.domain) params.domain = filters.domain;
    if (filters.language) params.language = filters.language;
    if (filters.template_type && filters.template_type !== 'any') {
        params.template_type = filters.template_type;
    }
    if (filters.country) params.country = filters.country;
    if (filters.q) params.q = filters.q;
    if (filters.only_favorites) params.only_favorites = true;
    if (filters.project_id != null) params.project_id = filters.project_id;
    if (filters.metric) params.metric = filters.metric;
    if (filters.period) params.period = filters.period;
    if (filters.min_total_sent != null) params.min_total_sent = filters.min_total_sent;
    if (filters.min_pageviews != null) params.min_pageviews = filters.min_pageviews;
    if (filters.page) params.page = filters.page;
    if (filters.per_page) params.per_page = filters.per_page;
    const response = await apiClient.get<TemplateLibraryListResponse>(
        '/template-library/templates',
        { params },
    );
    return response.data;
};

export const listFavoriteTemplateIds = async (): Promise<TemplateFavoriteIdsResponse> => {
    const response = await apiClient.get<TemplateFavoriteIdsResponse>(
        '/template-library/favorites',
    );
    return response.data;
};

export const addTemplateFavorite = async (
    templateId: number,
): Promise<TemplateFavoriteToggleResponse> => {
    const response = await apiClient.post<TemplateFavoriteToggleResponse>(
        `/template-library/favorites/${templateId}`,
    );
    return response.data;
};

export const removeTemplateFavorite = async (
    templateId: number,
): Promise<TemplateFavoriteToggleResponse> => {
    const response = await apiClient.delete<TemplateFavoriteToggleResponse>(
        `/template-library/favorites/${templateId}`,
    );
    return response.data;
};

// ─── Pastas ──────────────────────────────────────────────────────

export const getFolderTree = async (
    projectId?: number | null,
): Promise<TemplateFolderTree> => {
    const params: Record<string, any> = {};
    if (projectId != null) params.project_id = projectId;
    const response = await apiClient.get<TemplateFolderTree>(
        '/template-library/folders', { params },
    );
    return response.data;
};

export const createFolder = async (data: {
    name: string;
    project_id?: number | null;
    parent_id?: number | null;
}): Promise<{ success: boolean; id?: number }> => {
    const response = await apiClient.post('/template-library/folders', data);
    return response.data;
};

export const updateFolder = async (
    folderId: number,
    data: {
        name?: string;
        parent_id?: number | null;
        // project_id presente no body = migrar de escopo (cascata: pasta +
        // subpastas + templates dentro). null = pessoal.
        project_id?: number | null;
    },
): Promise<{ success: boolean; id?: number }> => {
    const response = await apiClient.patch(
        `/template-library/folders/${folderId}`, data,
    );
    return response.data;
};

export const deleteFolder = async (
    folderId: number,
): Promise<{ success: boolean; id?: number }> => {
    const response = await apiClient.delete(`/template-library/folders/${folderId}`);
    return response.data;
};

// ─── Templates próprios ──────────────────────────────────────────

export const listCustomTemplates = async (
    projectId?: number | null,
    folderId?: number | null,
): Promise<CustomTemplateListResponse> => {
    const params: Record<string, any> = {};
    if (projectId != null) params.project_id = projectId;
    if (folderId != null) params.folder_id = folderId;
    const response = await apiClient.get<CustomTemplateListResponse>(
        '/template-library/custom-templates', { params },
    );
    return response.data;
};

export const getCustomTemplate = async (id: number): Promise<CustomTemplate> => {
    const response = await apiClient.get<CustomTemplate>(
        `/template-library/custom-templates/${id}`,
    );
    return response.data;
};

export const createCustomTemplate = async (
    data: CustomTemplateCreatePayload,
): Promise<CustomTemplate> => {
    const response = await apiClient.post<CustomTemplate>(
        '/template-library/custom-templates', data,
    );
    return response.data;
};

export const updateCustomTemplate = async (
    id: number,
    data: CustomTemplateUpdatePayload,
): Promise<CustomTemplate> => {
    const response = await apiClient.patch<CustomTemplate>(
        `/template-library/custom-templates/${id}`, data,
    );
    return response.data;
};

export const deleteCustomTemplate = async (
    id: number,
): Promise<{ success: boolean; id?: number }> => {
    const response = await apiClient.delete(
        `/template-library/custom-templates/${id}`,
    );
    return response.data;
};

export const cloneTemplateToLibrary = async (
    data: CustomTemplateClonePayload,
): Promise<CustomTemplate> => {
    const response = await apiClient.post<CustomTemplate>(
        '/template-library/custom-templates/clone', data,
    );
    return response.data;
};

// ─── Aprovação Meta de templates UTILITY ─────────────────────────

export type MetaApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'ERROR';

export interface TemplateMetaApprovalRow {
    id: number;
    custom_template_id: number;
    page_id: string;
    meta_template_id?: string | null;
    status: MetaApprovalStatus;
    rejection_reason?: string | null;
    submitted_at?: string | null;
    approved_at?: string | null;
    last_polled_at?: string | null;
}

export interface TemplateMetaSubmissionResult {
    page_id: string;
    status: MetaApprovalStatus;
    meta_template_id?: string | null;
    reason?: string | null;
    detail?: string | null;
}

export interface SubmitTemplateMetaResponse {
    template_id: number;
    submitted_pages: number;
    results: TemplateMetaSubmissionResult[];
}

export const submitTemplateToMeta = async (
    templateId: number,
    body: { page_ids?: string[]; auto?: boolean },
): Promise<SubmitTemplateMetaResponse> => {
    const response = await apiClient.post<SubmitTemplateMetaResponse>(
        `/template-library/custom-templates/${templateId}/submit-meta`,
        body,
    );
    return response.data;
};

export const listTemplateMetaApprovals = async (
    templateId: number,
): Promise<{ items: TemplateMetaApprovalRow[]; total: number }> => {
    const response = await apiClient.get<{ items: TemplateMetaApprovalRow[]; total: number }>(
        `/template-library/custom-templates/${templateId}/approvals`,
    );
    return response.data;
};

export interface TemplateApprovalCounts {
    total: number;
    approved: number;
    pending: number;
    rejected: number;
    errored: number;
}

export type ApprovalScope = 'all' | 'esteira' | 'broadcast';

export const getTemplateApprovalSummary = async (
    templateIds: number[],
    scope: ApprovalScope = 'all',
): Promise<Record<string, TemplateApprovalCounts>> => {
    if (templateIds.length === 0) return {};
    const response = await apiClient.get<{ items: Record<string, TemplateApprovalCounts> }>(
        '/template-library/custom-templates/approvals/summary',
        { params: { ids: templateIds.join(','), scope } },
    );
    return response.data.items ?? {};
};

// ─── Status agregado de aprovação Meta da pasta (informacional) ──

export interface FolderApprovalStatus {
    folder_id: number;
    templates_total: number;
    counts: TemplateApprovalCounts;
    esteiras: { id: number; name: string }[];
}

export const getFolderApprovalStatus = async (
    folderId: number,
    scope: ApprovalScope = 'all',
): Promise<FolderApprovalStatus> => {
    const response = await apiClient.get<FolderApprovalStatus>(
        `/template-library/folders/${folderId}/approval-status`,
        { params: { scope } },
    );
    return response.data;
};

// ─── Breakdown de erros Meta agregados por categoria (UI informativa) ──

export interface FolderMetaErrorCategory {
    key: string;
    label: string;
    qty: number;
    pages_affected: number;
    sample: string | null;
}

export interface FolderMetaErrorBreakdown {
    folder_id: number;
    total_errors: number;
    by_category: FolderMetaErrorCategory[];
}

export const getFolderMetaErrorBreakdown = async (
    folderId: number,
    scope: ApprovalScope = 'all',
): Promise<FolderMetaErrorBreakdown> => {
    const response = await apiClient.get<FolderMetaErrorBreakdown>(
        `/template-library/folders/${folderId}/meta-error-breakdown`,
        { params: { scope } },
    );
    return response.data;
};

export const bulkCreateCustomTemplates = async (
    data: CustomTemplateBulkCreatePayload,
): Promise<CustomTemplateBulkCreateResponse> => {
    const response = await apiClient.post<CustomTemplateBulkCreateResponse>(
        '/template-library/custom-templates/bulk', data,
    );
    return response.data;
};

export const batchMoveCustomTemplates = async (data: {
    ids: number[];
    folder_id?: number | null;
}): Promise<CustomTemplateBatchMoveResponse> => {
    const response = await apiClient.patch<CustomTemplateBatchMoveResponse>(
        '/template-library/custom-templates/batch-move', data,
    );
    return response.data;
};

export interface CustomTemplateBatchDeleteResponse {
    deleted: number;
    skipped: number;
}

export const batchDeleteCustomTemplates = async (data: {
    ids: number[];
}): Promise<CustomTemplateBatchDeleteResponse> => {
    // POST (não DELETE) pra não esbarrar em proxy que stripa body de DELETE
    const response = await apiClient.post<CustomTemplateBatchDeleteResponse>(
        '/template-library/custom-templates/batch-delete', data,
    );
    return response.data;
};

// ─── Sync do Flow ────────────────────────────────────────────────

export interface FlowFunnelItem {
    id: number;
    nome: string;
    node_count: number;
    is_active: boolean;
}

export const listFlowFunnels = async (): Promise<{ funnels: FlowFunnelItem[] }> => {
    const response = await apiClient.get<{ funnels: FlowFunnelItem[] }>(
        '/template-library/flow-funnels',
    );
    return response.data;
};

export const syncTemplatesFromFlow = async (data: {
    project_id?: number | null;
    folder_id?: number | null;
    funnel_ids: number[];
}): Promise<{ created: number; updated: number; skipped: number }> => {
    const response = await apiClient.post(
        '/template-library/custom-templates/sync-from-flow', data,
    );
    return response.data;
};
